import { TASK_SORT_FIELDS } from '@modules/tarefas/constants/Text'
import {
  findComment,
  walkTaskAndMutate,
  applyAddAttachments,
  applyRemoveAttachment,
} from '@modules/tarefas/utils/taskCache'
import { _findFolderNode } from './mutations-internal'

// Set of valid sort field keys (excluding the Manual `null` default). Used by
// the sort-chain mutations to coerce/drop any unknown field defensively. A Set
// gives O(1) membership checks via `.has()`.
const _VALID_SORT_FIELDS = new Set(
  TASK_SORT_FIELDS.map(function (f) {
    return f.field
  }).filter(function (field) {
    return field != null
  })
)

function _isValidSortField(field) {
  return _VALID_SORT_FIELDS.has(field)
}

function _coerceDirection(direction) {
  return direction === 'desc' ? 'desc' : 'asc'
}

// Normalizes an arbitrary value into a clean sort chain: keeps only entries
// whose `field` is valid, coerces direction to 'asc'/'desc', and dedupes by
// field (first occurrence wins). Returns a fresh array.
function _normalizeChain(value) {
  const list = Array.isArray(value) ? value : []
  const seen = {}
  const chain = []
  for (const entry of list) {
    const field = entry && entry.field
    if (!_isValidSortField(field)) continue
    if (seen[field]) continue
    seen[field] = true
    chain.push({ field: field, direction: _coerceDirection(entry && entry.direction) })
  }
  return chain
}

export function SET_FOLDERS(state, folders) {
  state.folders = folders
}

// Replaces the whole sort chain with a normalized copy of `chain`. Invalid
// fields are dropped, directions coerced, fields deduped (first wins).
// Components dispatch `setSortChain` — they must never commit this directly.
export function SET_SORT_CHAIN(state, chain) {
  state.sortChain = _normalizeChain(chain)
}

// Builds the next chain for a field already present at `idx`:
//   · asc  → flip to desc (in place, keeping precedence).
//   · desc → remove it (precedence renumbers implicitly via array order).
function _cycleExisting(chain, idx, field) {
  const next = chain.slice()
  if (chain[idx].direction === 'asc') {
    next.splice(idx, 1, { field: field, direction: 'desc' })
  } else {
    next.splice(idx, 1)
  }
  return next
}

// Cycles one column through asc → desc → off, building/editing the chain.
// When the field is absent: append `{field,'asc'}` (additive / shift+click) or
// REPLACE the chain with `[{field,'asc'}]` (non-additive). Components dispatch
// `cycleSortColumn` — never commit directly.
export function CYCLE_SORT_COLUMN(state, payload) {
  const safe = payload && typeof payload === 'object' ? payload : {}
  const field = safe.field
  if (!_isValidSortField(field)) return
  const current = _normalizeChain(state.sortChain)
  const idx = current.findIndex(function (e) {
    return e.field === field
  })
  if (idx !== -1) {
    state.sortChain = _cycleExisting(current, idx, field)
    return
  }
  const appended = { field: field, direction: 'asc' }
  state.sortChain = safe.additive ? current.concat([appended]) : [appended]
}

export function CLEAR_SORT_CHAIN(state) {
  state.sortChain = []
}

// Back-compat thin wrapper: writes a 1-element chain from the legacy
// `{ field, direction }` partial payload, reading the current PRIMARY as the
// base. `field: null` (or invalid) clears the chain (Manual). Kept so legacy
// callers of `setSort` keep working against the new chain slice.
export function SET_SORT(state, payload) {
  const safe = payload && typeof payload === 'object' ? payload : {}
  const primary = (Array.isArray(state.sortChain) && state.sortChain[0]) || {
    field: null,
    direction: 'asc',
  }
  let field = primary.field
  let direction = primary.direction
  if (Object.hasOwn(safe, 'field')) {
    field = _isValidSortField(safe.field) ? safe.field : null
  }
  if (Object.hasOwn(safe, 'direction')) {
    direction = _coerceDirection(safe.direction)
  }
  if (field == null) {
    state.sortChain = []
  } else {
    state.sortChain = [{ field: field, direction: _coerceDirection(direction) }]
  }
}

export function SET_TASKS(state, tasks) {
  state.tasks = tasks
  // Sinaliza que o board já teve pelo menos um fetch resolvido. Reloads
  // posteriores (Filtrar/Limpar, troca de sprint, etc.) usam só o
  // `v-progress-linear` no topo de `task-list-view` em vez de
  // substituir o conteúdo por skeleton.
  state.tasksFetchedAtLeastOnce = true
  // AC-1.7: uma carga que chegou até aqui limpa o erro anterior. Sem isso o
  // estado de erro fica preso na tela depois de um retry bem-sucedido.
  state.tasksLoadError = null
}

// Acrescenta uma PÁGINA ao fim da lista, sem tocar no que já está na tela
// (spec-002 F4). Usado pela página 2+ do "Carregar mais" na visão rápida —
// `SET_TASKS` substituiria e o usuário perderia o que já havia carregado.
//
// Deduplica por `id`: uma linha que já esteja na lista não entra de novo. Sem
// isso, uma tarefa criada entre duas páginas empurraria o conjunto e a mesma
// linha voltaria na página seguinte — a duplicata quebraria o `:key` do v-for e
// a seleção em lote.
export function APPEND_TASKS(state, tasks) {
  const incoming = Array.isArray(tasks) ? tasks : []
  if (!incoming.length) {
    state.tasksFetchedAtLeastOnce = true
    state.tasksLoadError = null
    return
  }
  const known = new Set((state.tasks || []).map((task) => String(task.id)))
  const fresh = incoming.filter((task) => !known.has(String(task.id)))
  state.tasks = (state.tasks || []).concat(fresh)
  state.tasksFetchedAtLeastOnce = true
  state.tasksLoadError = null
}

// Paginação da visão rápida: `{ page, pageSize, total }`. `total` é o número de
// LINHAS-RAIZ do conjunto inteiro (não das carregadas) — é o que permite mostrar
// "Mostrando N de TOTAL" sem uma segunda chamada.
export function SET_QUICK_VIEW_PAGINATION(state, pagination) {
  state.quickViewPagination = pagination || { page: 1, pageSize: 0, total: 0 }
}

// Erro de carga da lista (AC-1.7). `null` limpa; `{ message }` sinaliza que a
// última tentativa falhou. Comitado por `loadTasks` no ramo de resposta ≠ 200,
// sempre DEPOIS de `SET_TASKS([])` — a ordem importa porque `SET_TASKS` zera
// este campo.
export function SET_TASKS_LOAD_ERROR(state, payload) {
  state.tasksLoadError = payload || null
}

// Sinal de perda de acesso (W8/F6). A action `loadTasks` commita este mutation
// quando o recurso selecionado responde 404. A view (`view-task-board`) observa
// via `store.subscribe` e orquestra snack + reload da árvore + limpar seleção.
// `at` (timestamp) garante identidade nova a cada sinal — útil caso algo observe
// `state.accessLost` diretamente no futuro.
export function SIGNAL_ACCESS_LOST(state, payload) {
  const safe = payload && typeof payload === 'object' ? payload : {}
  state.accessLost = { recurso: safe.recurso || null, at: Date.now() }
}

export function SET_ACTIVE_FOLDER_ID(state, folderId) {
  state.activeFolderId = folderId
}

export function SET_ACTIVE_SPRINT_ID(state, sprintId) {
  state.activeSprintId = sprintId
}

export function SET_ACTIVE_QUICK_VIEW(state, viewKey) {
  state.activeQuickView = viewKey
}

export function SET_QUICK_VIEW_COUNTS(state, counts) {
  state.quickViewCounts = counts || {}
}

export function SET_LOADING(state, loading) {
  state.loading = loading
}

export function SET_OVERVIEW_STATS(state, stats) {
  state.overviewStats = stats
}

// Closed-task visibility (session-only). AC-9 — Board "Mostrar fechadas" toggle.
export function SET_INCLUDE_CLOSED(state, value) {
  state.includeClosed = !!value
}

// Client-side search query (filtra por título via `currentSprintTasks`).
// String vazia/whitespace → limpa o filtro.
export function SET_SEARCH_QUERY(state, value) {
  state.searchQuery = typeof value === 'string' ? value : ''
}

// Client-side status filter (AC-13) — array of statusIds applied over
// `currentSprintTasks`. Defensive: coerces to a clean string-id array.
export function SET_STATUS_FILTER(state, value) {
  if (!Array.isArray(value)) {
    state.statusFilter = []
    return
  }
  state.statusFilter = value.filter(function (id) {
    return id != null && id !== ''
  })
}

export function ADD_TASK(state, task) {
  if (!task || task.parentId == null) {
    state.tasks.push(task)
    return
  }

  function appendUnderParent(list) {
    if (!Array.isArray(list)) return false
    for (let i = 0; i < list.length; i++) {
      const node = list[i]
      if (!node) continue
      if (node.id === task.parentId) {
        const merged = {
          ...node,
          subtarefas: (Array.isArray(node.subtarefas) ? node.subtarefas : []).concat([task]),
        }
        list.splice(i, 1, merged)
        return true
      }
      if (appendUnderParent(node.subtarefas)) {
        const updatedParent = { ...node }
        list.splice(i, 1, updatedParent)
        return true
      }
    }
    return false
  }

  if (appendUnderParent(state.tasks)) return

  // Parent not found in cache — fall back to root so the task is not lost.
  state.tasks.push(task)
}

// Funde a resposta do backend sobre o nó em cache, preservando `subtarefas`
// quando a resposta não traz netos (o endpoint de detalhe de subtarefa só
// devolve filhos diretos).
//
// ★ O MERGE VALE TAMBÉM PARA A RAIZ. Antes a raiz era SUBSTITUÍDA pela resposta
// crua e a subtarefa era fundida — assimetria que apagava cada campo sintético
// que só o `GET /tasks` produz e o `PUT /tasks/:id` não devolve. O caso real:
// uma tarefa ESPELHADA perdia `isMirrored`/`mirroredInto` ao ter status ou
// prioridade alterados, e `_sprintBase` (getters) voltava a compará-la pelo
// `sprintId` de ORIGEM — ela sumia da lista de destino até dar F5.
function _mergeTaskNode(node, updatedTask) {
  const preservedChildren = Array.isArray(node.subtarefas) ? node.subtarefas : []
  const nextChildren = Array.isArray(updatedTask.subtarefas)
    ? updatedTask.subtarefas
    : preservedChildren
  return { ...node, ...updatedTask, subtarefas: nextChildren }
}

export function UPDATE_TASK(state, updatedTask) {
  // Funde no topo quando é tarefa raiz; senão caminha nas `subtarefas`
  // aninhadas (qualquer profundidade) e funde no lugar, para a linha
  // correspondente re-renderizar.
  const idx = state.tasks.findIndex(function (t) {
    return t.id === updatedTask.id
  })
  if (idx !== -1) {
    state.tasks.splice(idx, 1, _mergeTaskNode(state.tasks[idx], updatedTask))
    return
  }

  function mergeSubtasks(list) {
    if (!Array.isArray(list) || !list.length) return false
    for (let i = 0; i < list.length; i++) {
      const node = list[i]
      if (!node) continue
      if (node.id === updatedTask.id) {
        list.splice(i, 1, _mergeTaskNode(node, updatedTask))
        return true
      }
      if (mergeSubtasks(node.subtarefas)) return true
    }
    return false
  }

  for (const rootTask of state.tasks) {
    if (mergeSubtasks(rootTask.subtarefas)) return
  }
}

// Optimistic status patch: applies ONLY `statusId` (+ the embedded
// `statusCategory` fallback used by the quick-view buckets) so the card/row
// lands in the target column/group before the PUT resolves. Every other field
// — including `subtarefas` — is preserved, because this runs against the local
// task, not a server payload. `UPDATE_TASK` still writes the authoritative
// response afterwards; on failure the caller replays this with the previous
// values to roll back.
export function PATCH_TASK_STATUS(state, { id, statusId, statusCategory } = {}) {
  if (id == null) return

  function patch(node) {
    return { ...node, statusId: statusId, statusCategory: statusCategory }
  }

  const idx = state.tasks.findIndex(function (t) {
    return t && t.id === id
  })
  if (idx !== -1) {
    state.tasks.splice(idx, 1, patch(state.tasks[idx]))
    return
  }

  function patchSubtasks(list) {
    if (!Array.isArray(list) || !list.length) return false
    for (let i = 0; i < list.length; i++) {
      const node = list[i]
      if (!node) continue
      if (node.id === id) {
        list.splice(i, 1, patch(node))
        return true
      }
      if (patchSubtasks(node.subtarefas)) return true
    }
    return false
  }

  for (const rootTask of state.tasks) {
    if (rootTask && patchSubtasks(rootTask.subtarefas)) return
  }
}

// Remove QUALQUER tarefa cujo id esteja em `taskIds`, em qualquer nível da
// árvore (raiz ou `subtarefas[]` aninhada, profundidade arbitrária). Ids
// ausentes são ignorados sem erro.
//
// [T005/D1] Antes só a raiz era filtrada — subtarefas selecionáveis no lote
// (`task-list-view.vue`) eram deletadas no backend e continuavam na tela.
// A varredura segue o mesmo idioma recursivo de `_walkFolders`/`ADD_TASK`:
// percorre a lista, remove o nó por `splice` quando o id casa, e ao voltar de
// um nível aninhado faz `splice` do pai com cópia rasa para o Vue 2 detectar
// a mudança na subárvore (arrays reativos do Vue 2 não avisam sobre mutação
// de propriedade de objeto interno sem esse truque).
function _removeTasksFromList(list, idsSet) {
  if (!Array.isArray(list) || !list.length) return false
  let changed = false
  for (let i = list.length - 1; i >= 0; i--) {
    const node = list[i]
    if (!node) continue
    if (idsSet.has(node.id)) {
      list.splice(i, 1)
      changed = true
      continue
    }
    if (_removeTasksFromList(node.subtarefas, idsSet)) {
      list.splice(i, 1, { ...node })
      changed = true
    }
  }
  return changed
}

export function REMOVE_TASKS(state, taskIds) {
  const ids = Array.isArray(taskIds) ? taskIds : [taskIds]
  const idsSet = new Set(ids)
  if (!idsSet.size) return
  _removeTasksFromList(state.tasks, idsSet)
}

// Delete individual — delega para a mesma varredura recursiva de
// REMOVE_TASKS, senão o delete de uma linha tem o mesmo defeito D1 que o
// delete em lote (subtarefa some do banco mas continua na tela).
export function REMOVE_TASK(state, taskId) {
  REMOVE_TASKS(state, [taskId])
}

// --- Árvore de pastas (pasta dentro de pasta) ---------------------------
// `state.folders` guarda só as raízes; cada nó carrega `subpastas[]` (até 3
// níveis). As mutations de pasta abaixo percorrem essa árvore recursivamente,
// mesmo padrão que ADD_TASK/UPDATE_TASK já usam para `subtarefas`.

// Procura `folderId` em qualquer nível e chama `visit(list, index, node)` no
// nó encontrado — `list`/`index` permitem trocar ou remover o próprio nó. Ao
// voltar de um nível aninhado faz splice do pai com uma cópia rasa para o
// Vue 2 detectar a mudança na subárvore (mesmo truque do `_replaceParentRef`
// usado pelas mutations de tag). Esse splice de volta é um efeito colateral
// desejado aqui e é justamente o motivo de existir um `_findFolderNode`
// separado, puro, para quem só precisa LER o nó.
function _walkFolders(list, folderId, visit) {
  if (!Array.isArray(list) || !list.length) return false
  for (let i = 0; i < list.length; i++) {
    const node = list[i]
    if (!node) continue
    if (node.id === folderId) {
      visit(list, i, node)
      return true
    }
    if (_walkFolders(node.subpastas, folderId, visit)) {
      list.splice(i, 1, { ...node })
      return true
    }
  }
  return false
}

// O PUT de pasta devolve só o cabeçalho (name/parentId/…). Aceitar um
// `subpastas: []` / `sprints: []` vazio do backend apagaria a subárvore e as
// listas que o GET /folders já trouxe — daí a preferência pelo que está no
// cache quando o payload não traz nada de útil.
function _preserveList(incoming, cached) {
  if (Array.isArray(incoming) && incoming.length) return incoming
  if (Array.isArray(cached)) return cached
  return Array.isArray(incoming) ? incoming : []
}

export function ADD_FOLDER(state, folder) {
  if (!folder || folder.parentId == null) {
    state.folders.push(folder)
    return
  }

  const inserted = _walkFolders(state.folders, folder.parentId, function (list, i, node) {
    const merged = {
      ...node,
      subpastas: (Array.isArray(node.subpastas) ? node.subpastas : []).concat([folder]),
    }
    list.splice(i, 1, merged)
  })
  if (inserted) return

  // Mãe fora do cache — cai na raiz para a pasta não sumir da sidebar.
  state.folders.push(folder)
}

export function UPDATE_FOLDER(state, updatedFolder) {
  if (!updatedFolder) return
  _walkFolders(state.folders, updatedFolder.id, function (list, i, node) {
    const merged = {
      ...node,
      ...updatedFolder,
      subpastas: _preserveList(updatedFolder.subpastas, node.subpastas),
      sprints: _preserveList(updatedFolder.sprints, node.sprints),
    }
    list.splice(i, 1, merged)
  })
}

export function REMOVE_FOLDER(state, folderId) {
  _walkFolders(state.folders, folderId, function (list, i) {
    list.splice(i, 1)
  })
  state.tasks = state.tasks.filter(function (t) {
    return t.folderId !== folderId
  })
}
export function ADD_SPRINT(state, { folderId, sprint }) {
  const folder = _findFolderNode(state.folders, folderId)
  if (folder) {
    if (!Array.isArray(folder.sprints)) folder.sprints = []
    folder.sprints.push(sprint)
  }
}

export function UPDATE_SPRINT(state, { folderId, sprint }) {
  const folder = _findFolderNode(state.folders, folderId)
  if (folder && Array.isArray(folder.sprints)) {
    const idx = folder.sprints.findIndex(function (s) {
      return s.id === sprint.id
    })
    if (idx !== -1) {
      folder.sprints.splice(idx, 1, sprint)
    }
  }
}

export function REMOVE_SPRINT(state, { folderId, sprintId }) {
  const folder = _findFolderNode(state.folders, folderId)
  if (folder && Array.isArray(folder.sprints)) {
    folder.sprints = folder.sprints.filter(function (s) {
      return s.id !== sprintId
    })
  }
  state.tasks = state.tasks.filter(function (t) {
    return t.sprintId !== sprintId
  })
}

export function ADD_COMMENT(state, { taskId, comment }) {
  const task = state.tasks.find(function (t) {
    return t.id === taskId
  })
  if (task) {
    if (!task.comments) task.comments = []
    task.comments.push(comment)
  }
}

export function UPDATE_COMMENT(state, { taskId, comment }) {
  const task = state.tasks.find(function (t) {
    return t.id === taskId
  })
  if (task && task.comments) {
    const idx = task.comments.findIndex(function (c) {
      return c.id === comment.id
    })
    if (idx !== -1) {
      task.comments.splice(idx, 1, comment)
    }
  }
}

export function REMOVE_COMMENT(state, { taskId, commentId }) {
  const task = state.tasks.find(function (t) {
    return t.id === taskId
  })
  if (task && task.comments) {
    task.comments = task.comments.filter(function (c) {
      return c.id !== commentId
    })
  }
}

export function REMOVE_COMMENT_ATTACHMENT(state, { taskId, commentId, attachmentId }) {
  const comment = findComment(state, taskId, commentId)
  if (!comment || !comment.attachments) return
  comment.attachments = comment.attachments.filter(function (a) {
    return a.id !== attachmentId
  })
}

export function ADD_TASK_ATTACHMENTS(state, { taskId, attachments }) {
  if (!taskId) return
  walkTaskAndMutate(state.tasks, taskId, function (node) {
    applyAddAttachments(node, attachments)
  })
  if (state.taskDetail && state.taskDetail.id === taskId) {
    const detail = { ...state.taskDetail }
    applyAddAttachments(detail, attachments)
    state.taskDetail = detail
  }
}

export function REMOVE_TASK_ATTACHMENT(state, { taskId, attachmentId }) {
  if (!taskId || !attachmentId) return
  walkTaskAndMutate(state.tasks, taskId, function (node) {
    applyRemoveAttachment(node, attachmentId)
  })
  if (state.taskDetail && state.taskDetail.id === taskId) {
    const detail = { ...state.taskDetail }
    applyRemoveAttachment(detail, attachmentId)
    state.taskDetail = detail
  }
}
